import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ScanFace, Sparkles, ShieldCheck, Camera, MessageCircle, ImagePlus,
  Brain, Microscope, Clock, ArrowDown, CheckCircle2, Star, ChevronDown, Lock, Gift,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { GLOW } from "@/constants/testIds";
import SocialProof from "@/components/SocialProof";
import Testimonials from "@/components/Testimonials";
import ScoreRing from "@/components/ScoreRing";

const HERO = "https://images.unsplash.com/photo-1674932668403-33398b81c92f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const MAX_MB = 8;

// Foto fotorealistiche della STESSA persona (espressione neutra, struccata) generate come esempio anonimo
const MAP_FRONTAL = "https://static.prod-images.emergentagent.com/jobs/adec208f-591b-465c-9f38-82c2f040c49e/images/3491d5683a467758cc16886186e1edbec1cd20d73d776f20954ad97b542d9dde.jpeg";
const MAP_LATERAL = "https://static.prod-images.emergentagent.com/jobs/adec208f-591b-465c-9f38-82c2f040c49e/images/24ba01436873e425271bee6e861b2ecc642420e5b02ddba0115261e6fb008804.jpeg";
const SEV = { alto: "#C87979", medio: "#D4AF37", basso: "#8BA888" };
const SEV_LABEL = { alto: "ALTO", medio: "MEDIO", basso: "BASSO" };
const MAP_FRONT_POINTS = [
  { n: 1, x: 0.45, y: 0.62, sev: "alto", label: "Solchi naso-genieni evidenti" },
  { n: 2, x: 0.50, y: 0.78, sev: "alto", label: "Rilassamento ovale e perdita di definizione mandibolare" },
  { n: 3, x: 0.50, y: 0.30, sev: "medio", label: "Linee della glabella e rughe frontali" },
  { n: 4, x: 0.44, y: 0.46, sev: "medio", label: "Solco lacrimale e cedimento zona palpebrale inferiore" },
  { n: 5, x: 0.58, y: 0.56, sev: "basso", label: "Disidratazione superficiale e texture irregolare sulle guance" },
];
const MAP_LAT_POINTS = [
  { n: 6, x: 0.58, y: 0.66, sev: "alto", label: "Cedimento del terzo inferiore e linee marionetta" },
  { n: 7, x: 0.52, y: 0.82, sev: "alto", label: "Rilassamento cutaneo collo (platisma e pelle)" },
  { n: 8, x: 0.56, y: 0.40, sev: "medio", label: "Zampe di gallina visibili al profilo" },
  { n: 9, x: 0.58, y: 0.53, sev: "medio", label: "Cedimento zigomatico — perdita volume medio-faccia" },
];

// Anteprima costruita su un report reale e anonimo (nessuna foto della persona)
const PREVIEW = {
  score: 62,
  summary: "Pelle matura, fototipo III e sottotono caldo. La luminosità di fondo è un punto di forza; i margini di miglioramento su tono e definizione dell'ovale sono concreti e raggiungibili.",
  age: { anagrafica: 56, cutanea: 58, giudizio: "In linea, con margini di miglioramento" },
  profile: [["Tipologia", "Mista"], ["Sottotono", "Caldo"], ["Fototipo", "III"], ["Elasticità", "Ridotta"]],
  indicators: [
    { label: "Luminosità", value: 60 },
    { label: "Idratazione", value: 52 },
    { label: "Texture", value: 55 },
    { label: "Elasticità · Tono", value: 38 },
    { label: "Definizione ovale", value: 35 },
  ],
  zones: [
    { zone: "Solchi naso-genieni", grade: 6 },
    { zone: "Linee marionetta", grade: 6 },
    { zone: "Zampe di gallina", grade: 5 },
    { zone: "Fronte", grade: 5 },
  ],
  concern: "Rilassamento",
  concernText: "Una routine antiage rigenerativa con peptidi tensori, vitamina C stabile e acido ialuronico ridà struttura e definisce l'ovale — con costanza, effetti visibili già in 4–6 settimane.",
};
const gradeColor = (g) => (g >= 6 ? "#C87979" : g >= 4 ? "#D4AF37" : "#8BA888");

const STEPS = [
  { icon: Camera, title: "Carichi 2 selfie", desc: "Una foto frontale e una di profilo, in buona luce. Nessuna app da installare: bastano 20 secondi." },
  { icon: Brain, title: "L'AI legge la tua pelle", desc: "Il nostro analizzatore osserva idratazione, texture, luminosità, pori e segni del tempo come farebbe un'esperta al tuo fianco." },
  { icon: Sparkles, title: "Ricevi il tuo Glow Score", desc: "Un report personalizzato con punteggio, mappa della pelle e la routine di cosmetici professionali giusta per te." },
];

const FAQS = [
  { q: "L'analisi è davvero gratuita?", a: "Sì, al 100%. Ottieni il tuo Glow Score e una consulenza personalizzata senza alcun costo e senza impegno d'acquisto." },
  { q: "Come fa l'AI a essere affidabile?", a: "Il nostro analizzatore è stato costruito su oltre 1.874 anamnesi reali del viso realizzate in tutta Italia e sul metodo natural antiage di Skin Lovers. Non è un giochino: è un sistema esperto." },
  { q: "Cosa ricevo alla fine?", a: "Un report completo con il tuo Glow Score, la mappa dei tuoi punti di forza e di attenzione e una routine su misura con i cosmetici professionali Skin Lovers, anche via email e WhatsApp." },
];

const UploadBox = ({ label, hint, file, onPick, testId, inputTestId }) => {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [preview, setPreview] = useState(null);
  const handle = (f) => {
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`Foto troppo grande (max ${MAX_MB}MB)`); return; }
    onPick(f);
    try { setPreview(URL.createObjectURL(f)); } catch (_) { setPreview(null); }
  };
  return (
    <div
      data-testid={testId}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files?.[0]); }}
      className={`cursor-pointer rounded-2xl border-2 border-dashed p-6 text-center transition-all bg-white/70 backdrop-blur-xl
        ${drag ? "border-[#C17F59] bg-[#C17F59]/5" : "border-[#C17F59]/40 hover:border-[#C17F59]/70"}`}
      style={{ boxShadow: "0 8px 32px rgba(45,40,37,0.06)" }}
    >
      <input ref={inputRef} data-testid={inputTestId} type="file" accept="image/*,.heic,.heif"
        className="hidden" onChange={(e) => handle(e.target.files?.[0])} />
      {preview ? (
        <img src={preview} alt={label} className="mx-auto h-28 w-28 rounded-xl object-cover shadow-md" />
      ) : (
        <div className="flex flex-col items-center gap-2 py-3">
          <div className="rounded-full bg-[#C17F59]/10 p-3"><ScanFace className="h-6 w-6 text-[#C17F59]" /></div>
          <ImagePlus className="h-4 w-4 text-[#C17F59]/60" />
        </div>
      )}
      <p className="mt-3 font-medium text-sm text-[#2D2825]">{label}</p>
      <p className="text-xs text-[#7A7571]">{hint}</p>
    </div>
  );
};

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const SkinMapPanel = ({ title, src, points, objectPos = "center" }) => (
  <div className="overflow-hidden rounded-2xl border border-[#E8E6E1] bg-white">
    <div className="bg-[#2D2825] px-4 py-2 text-xs uppercase tracking-[0.2em] text-white">{title}</div>
    <div className="relative aspect-[3/2]">
      <img src={src} alt={`Esempio ${title}`} loading="lazy"
        className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: objectPos }} />
      {points.map((p) => (
        <div key={p.n}
          className="absolute flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white text-xs font-bold text-white shadow-md"
          style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%`, background: SEV[p.sev] }}>
          {p.n}
        </div>
      ))}
    </div>
    <div className="space-y-1.5 p-4">
      {points.map((p) => (
        <div key={p.n} className="flex items-center gap-2 text-sm">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] text-white" style={{ background: SEV[p.sev] }}>{p.n}</span>
          <span className="flex-1 leading-snug text-[#2D2825]">{p.label}</span>
          <span className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-mono" style={{ background: `${SEV[p.sev]}22`, color: SEV[p.sev] }}>{SEV_LABEL[p.sev]}</span>
        </div>
      ))}
    </div>
  </div>
);

const ReportPreview = () => (
  <div data-testid="report-preview" className="relative">
    <span className="absolute -top-3 left-6 z-10 inline-flex items-center gap-1.5 rounded-full bg-[#2D2825] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-white shadow-lg">
      <Sparkles className="h-3.5 w-3.5 text-[#C17F59]" /> Esempio di report reale
    </span>
    <div className="overflow-hidden rounded-3xl border border-[#E8E6E1] bg-white" style={{ boxShadow: "0 24px 60px rgba(45,40,37,0.10)" }}>
      {/* intestazione stile documento */}
      <div className="flex items-center justify-between border-b-2 border-[#2D2825] px-6 py-4 sm:px-8">
        <div>
          <p className="font-display text-xl tracking-tight text-[#2D2825]">SK<span className="text-[#C17F59]">+</span>L <span className="text-[10px] tracking-[0.3em] align-middle">SKIN LOVERS</span></p>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#7A7571]">Documento di consulenza · Skin Mapping</p>
        </div>
        <span className="hidden rounded-full bg-[#8BA888]/15 px-3 py-1 text-[11px] font-medium text-[#5f7d5c] sm:inline">N° 130</span>
      </div>

      {/* Skin Mapping: foto reali annotate (esempio anonimo, non AI) */}
      <div className="grid gap-5 p-6 sm:grid-cols-2 sm:p-8">
        <SkinMapPanel title="Frontale" src={MAP_FRONTAL} points={MAP_FRONT_POINTS} objectPos="center" />
        <SkinMapPanel title="Laterale" src={MAP_LATERAL} points={MAP_LAT_POINTS} objectPos="center" />
      </div>

      <div className="grid gap-8 border-t border-[#E8E6E1] p-6 sm:p-8 lg:grid-cols-[0.9fr_1.1fr]">
        {/* Colonna sinistra: score + età + cruccio */}
        <div>
          <div className="flex flex-col items-center rounded-3xl bg-[#FAF9F6] p-6 text-center">
            <ScoreRing score={PREVIEW.score} size={170} stroke={12} />
            <p className="mt-4 text-sm leading-relaxed text-[#5f5a55]">{PREVIEW.summary}</p>
          </div>

          <div className="mt-4 rounded-2xl bg-white p-4" style={{ borderLeft: "4px solid #C17F59", boxShadow: "0 8px 32px rgba(45,40,37,0.05)" }}>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7571]">Età della pelle</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#7A7571]">
              <span className="font-medium text-[#C17F59]">{PREVIEW.age.giudizio}</span>
              <span>Anagrafica <strong className="text-[#2D2825]">{PREVIEW.age.anagrafica}</strong></span>
              <span>Cutanea <strong className="text-[#2D2825]">{PREVIEW.age.cutanea}</strong></span>
            </div>
          </div>

          <div className="mt-4 rounded-2xl border border-[#C17F59]/25 bg-[#C17F59]/8 p-4">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#a96a47]">Obiettivo principale</p>
            <p className="mt-1 font-display text-lg text-[#2D2825]">{PREVIEW.concern}</p>
            <p className="mt-1.5 text-xs leading-relaxed text-[#5c5652]">{PREVIEW.concernText}</p>
          </div>
        </div>

        {/* Colonna destra: profilo + indicatori + aging */}
        <div className="space-y-6">
          <div>
            <h3 className="mb-3 font-display text-lg text-[#2D2825]">Profilo cutaneo</h3>
            <div className="grid grid-cols-2 gap-2">
              {PREVIEW.profile.map(([k, v]) => (
                <div key={k} className="flex items-center justify-between rounded-xl border border-[#E8E6E1] bg-white px-3 py-2">
                  <span className="text-[10px] uppercase tracking-[0.12em] text-[#7A7571]">{k}</span>
                  <span className="font-display text-sm text-[#2D2825]">{v}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="mb-3 font-display text-lg text-[#2D2825]">Indicatori clinici</h3>
            <div className="space-y-2.5">
              {PREVIEW.indicators.map((it) => (
                <div key={it.label}>
                  <div className="mb-1 flex justify-between text-xs"><span className="text-[#2D2825]">{it.label}</span><span className="text-[#7A7571]">{it.value}%</span></div>
                  <div className="h-2 overflow-hidden rounded-full bg-[#E8E6E1]">
                    <motion.div className="h-full rounded-full bg-[#C17F59]"
                      initial={{ width: 0 }} whileInView={{ width: `${it.value}%` }} viewport={{ once: true }}
                      transition={{ duration: 0.9 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="mb-3 font-display text-lg text-[#2D2825]">Analisi aging · 8 gradi per zona</h3>
            <div className="space-y-2.5">
              {PREVIEW.zones.map((z) => (
                <div key={z.zone} className="flex items-center gap-3">
                  <span className="w-36 shrink-0 text-xs text-[#2D2825]">{z.zone}</span>
                  <div className="flex flex-1 gap-1">
                    {[1,2,3,4,5,6,7,8].map((n) => (
                      <div key={n} className="flex h-5 flex-1 items-center justify-center rounded border text-[9px]"
                        style={n === z.grade ? { background: gradeColor(z.grade), color: "#fff", borderColor: gradeColor(z.grade) } : { borderColor: "#E8E6E1", color: "#B9B4AE" }}>{n}</div>
                    ))}
                  </div>
                  <span className="text-xs font-medium" style={{ color: gradeColor(z.grade) }}>{z.grade}/8</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default function Landing() {
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [frontal, setFrontal] = useState(null);
  const [lateral, setLateral] = useState(null);
  const [form, setForm] = useState({ nome: "", cognome: "", cellulare: "", email: "", data_nascita: "", sesso: "", focus_concern: "" });
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const setField = useCallback((k, v) => setForm((s) => ({ ...s, [k]: v })), []);

  const [dob, setDob] = useState({ d: "", m: "", y: "" });
  const setDobPart = useCallback((k, v) => {
    setDob((s) => {
      const next = { ...s, [k]: v };
      const iso = (next.d && next.m && next.y)
        ? `${next.y}-${String(next.m).padStart(2, "0")}-${String(next.d).padStart(2, "0")}`
        : "";
      setForm((f) => ({ ...f, data_nascita: iso }));
      return next;
    });
  }, []);

  const scrollToForm = useCallback(() => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const analyze = async () => {
    if (!frontal || !lateral) { toast.error("Carica sia la foto frontale che quella laterale"); scrollToForm(); return; }
    if (!form.nome.trim()) { toast.error("Inserisci il tuo nome"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("frontal", frontal);
      fd.append("lateral", lateral);
      Object.entries(form).forEach(([k, v]) => { if (k !== "focus_concern") fd.append(k, v); });
      const focus = (form.focus_concern || "").trim() || localStorage.getItem("glow_focus_concern") || "";
      fd.append("focus_concern", focus);
      const { data } = await api.post("/analyze", fd);
      navigate(`/r/${data.id}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      let msg;
      if (!err?.response) msg = "Connessione assente o instabile. Controlla la rete e riprova.";
      else if (err.response.status === 413) msg = "Foto troppo grande (max 8MB). Usa un'immagine più leggera.";
      else if (typeof d === "string") msg = d;
      else msg = "Invio non riuscito. Riprova tra qualche secondo.";
      toast.error(msg);
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-[#E8E6E1] bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#C17F59]";

  return (
    <div className="relative min-h-screen w-full bg-[#FAF9F6] text-[#2D2825] overflow-x-hidden">
      {/* ═══════════ HERO ═══════════ */}
      <section className="relative">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 pt-10 pb-16 sm:px-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14 lg:px-14 lg:pt-16">
          {/* Left: pitch */}
          <div className="flex flex-col justify-center">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="h-5 w-5 text-[#C17F59]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[#7A7571]">Skin Lovers Cosmetics</span>
            </div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="font-display tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
              La tua pelle, letta come<br className="hidden sm:block" /> da una <span className="text-[#C17F59]">vera esperta</span>.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-5 max-w-xl text-[#5f5a55] text-base leading-relaxed">
              Carica due selfie e ottieni <strong className="text-[#2D2825]">gratis</strong> il tuo <strong className="text-[#2D2825]">Glow Score</strong>:
              un'analisi professionale della pelle con la routine di cosmetici giusta per te.
              È il nostro modo di darti la stessa consulenza dei centri estetici, comodamente da casa.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.18 }}
              className="mt-6"><SocialProof /></motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.24 }}
              className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[#5f5a55]">
              {["Gratis, senza impegno", "Risultato in pochi minuti", "Routine su misura"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-[#8BA888]" />{t}
                </span>
              ))}
            </motion.div>

            <motion.button
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
              data-testid="hero-cta-scroll" onClick={scrollToForm}
              className="mt-8 inline-flex w-fit items-center justify-center gap-2 rounded-full bg-[#C17F59] px-8 py-4 text-white font-medium
                transition-all hover:bg-[#a96a47] active:scale-95 lg:hidden">
              Inizia l'analisi gratuita <ArrowDown className="h-4 w-4" />
            </motion.button>
          </div>

          {/* Right: the always-visible form */}
          <motion.div
            ref={formRef}
            initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="relative scroll-mt-8">
            <div className="absolute -inset-3 -z-10 rounded-[2rem] bg-gradient-to-br from-[#C17F59]/15 via-[#8BA888]/10 to-transparent blur-2xl" />
            <div className="rounded-3xl border border-[#E8E6E1] bg-white/85 p-6 backdrop-blur-xl sm:p-8"
              style={{ boxShadow: "0 24px 60px rgba(45,40,37,0.10)" }}>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#8BA888]/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#5f7d5c]">
                  <Gift className="h-3.5 w-3.5" /> Analisi gratuita
                </span>
              </div>
              <h2 className="mt-3 font-display text-2xl text-[#2D2825]">Scopri il tuo Glow Score</h2>
              <p className="mt-1 text-sm text-[#7A7571]">Carica due selfie per iniziare.</p>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <UploadBox label="Foto frontale" hint="Viso in primo piano" file={frontal}
                  onPick={setFrontal} testId={GLOW.frontalUpload} inputTestId={GLOW.frontalInput} />
                <UploadBox label="Foto laterale" hint="Profilo (dx o sx)" file={lateral}
                  onPick={setLateral} testId={GLOW.lateralUpload} inputTestId={GLOW.lateralInput} />
              </div>
              <p className="mt-2 text-xs text-[#7A7571] flex items-center gap-1">
                <ImagePlus className="h-3.5 w-3.5" /> JPG, PNG, HEIC · max {MAX_MB}MB per foto
              </p>

              <div className="mt-5 space-y-3">
                <input data-testid={GLOW.nomeInput} className={inputCls} placeholder="Il tuo nome"
                  value={form.nome} onChange={(e) => setField("nome", e.target.value)} />
                <label className="flex flex-col text-[10px] uppercase tracking-[0.15em] text-[#7A7571] gap-1">Data di nascita
                  <div className="grid grid-cols-3 gap-2">
                    <select data-testid="lead-dob-day" className={inputCls} value={dob.d}
                      onChange={(e) => setDobPart("d", e.target.value)}>
                      <option value="">Giorno</option>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <select data-testid="lead-dob-month" className={inputCls} value={dob.m}
                      onChange={(e) => setDobPart("m", e.target.value)}>
                      <option value="">Mese</option>
                      {["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"].map((mn, i) => <option key={i} value={i + 1}>{mn}</option>)}
                    </select>
                    <select data-testid="lead-dob-year" className={inputCls} value={dob.y}
                      onChange={(e) => setDobPart("y", e.target.value)}>
                      <option value="">Anno</option>
                      {Array.from({ length: new Date().getFullYear() - 14 - 1939 }, (_, i) => new Date().getFullYear() - 14 - i).map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </label>
                <label className="flex flex-col text-[10px] uppercase tracking-[0.15em] text-[#7A7571] gap-1">Sesso
                  <select data-testid="lead-sesso" className={inputCls}
                    value={form.sesso} onChange={(e) => setField("sesso", e.target.value)}>
                    <option value="">Seleziona…</option>
                    <option value="Femmina">Femmina</option>
                    <option value="Maschio">Maschio</option>
                    <option value="Altro">Altro</option>
                  </select>
                </label>
                <input data-testid="lead-focus-concern" className={inputCls}
                  placeholder="Cosa vorresti migliorare di più? (facoltativo)"
                  value={form.focus_concern}
                  onChange={(e) => {
                    const v = e.target.value;
                    setField("focus_concern", v);
                    if (v.trim()) localStorage.setItem("glow_focus_concern", v.trim());
                    else localStorage.removeItem("glow_focus_concern");
                  }} />
                <p className="text-[11px] text-[#7A7571]">
                  Es. rughe sopra il labbro · macchie · occhiaie · pori dilatati · rilassamento · disidratazione
                </p>
              </div>

              <div className="mt-4 flex items-start gap-2 rounded-xl bg-[#8BA888]/10 p-3">
                <MessageCircle className="h-4 w-4 text-[#5f7d5c] mt-0.5 shrink-0" />
                <p className="text-xs text-[#5f7d5c]">Mentre analizziamo la tua pelle ti chiederemo dove inviarti il report completo: lo riceverai <strong>anche via email e WhatsApp</strong>.</p>
              </div>

              <button data-testid={GLOW.analyzeButton} disabled={loading} onClick={analyze}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#C17F59] px-8 py-4 text-white font-medium
                  transition-all hover:bg-[#a96a47] active:scale-95 disabled:opacity-40">
                <Camera className="h-5 w-5" />
                {loading ? "Invio in corso…" : "Ottieni il mio Glow Score"}
              </button>

              <p className="mt-3 text-xs text-[#7A7571] flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" /> Foto e dati usati solo per l'analisi e conservati al massimo 30 giorni.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════ METODO / AUTORITÀ ═══════════ */}
      <section className="border-y border-[#E8E6E1] bg-white">
        <div className="mx-auto max-w-7xl px-6 py-14 sm:px-10 lg:px-14">
          <Reveal className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C17F59]">Non è un gioco. È un sistema esperto.</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl leading-tight">
              Costruito su <span className="text-[#C17F59]">1.874 analisi reali</span> del viso.
            </h2>
            <p className="mt-4 text-[#5f5a55] leading-relaxed">
              Il Glow Score nasce da oltre un anno di sviluppo e dal metodo <em>natural antiage</em> di Skin Lovers.
              Abbiamo trasformato migliaia di anamnesi reali, realizzate in tutta Italia, in un analizzatore AI proprietario
              che legge la tua pelle con l'occhio di un'esperta — non con una formula qualsiasi.
            </p>
          </Reveal>

          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {[
              { icon: Microscope, big: "1.874", small: "anamnesi reali del viso alla base del modello" },
              { icon: Clock, big: "1 anno", small: "di sviluppo del nostro analizzatore proprietario" },
              { icon: Brain, big: "AI + metodo", small: "tecnologia unita all'esperienza natural antiage" },
            ].map((s, i) => (
              <Reveal key={s.small} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-[#E8E6E1] bg-[#FAF9F6] p-6"
                  style={{ boxShadow: "0 8px 32px rgba(45,40,37,0.05)" }}>
                  <div className="mb-4 inline-flex rounded-full bg-[#C17F59]/10 p-3"><s.icon className="h-5 w-5 text-[#C17F59]" /></div>
                  <p className="font-display text-3xl text-[#2D2825]">{s.big}</p>
                  <p className="mt-1.5 text-sm text-[#7A7571]">{s.small}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ COME FUNZIONA ═══════════ */}
      <section className="mx-auto max-w-7xl px-6 py-16 sm:px-10 lg:px-14">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[#C17F59]">Come funziona</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl leading-tight">Il tuo Glow Score in 3 passi</h2>
        </Reveal>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.1}>
              <div className="relative h-full rounded-2xl border border-[#E8E6E1] bg-white p-7"
                style={{ boxShadow: "0 8px 32px rgba(45,40,37,0.05)" }}>
                <span className="absolute right-6 top-6 font-display text-4xl text-[#C17F59]/15">0{i + 1}</span>
                <div className="mb-5 inline-flex rounded-full bg-[#8BA888]/12 p-3"><s.icon className="h-6 w-6 text-[#5f7d5c]" /></div>
                <h3 className="font-display text-xl text-[#2D2825]">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#7A7571]">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1} className="mt-10">
          <button data-testid="steps-cta-scroll" onClick={scrollToForm}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2D2825] px-8 py-4 text-white font-medium
              transition-all hover:bg-[#463f3a] active:scale-95">
            <ScanFace className="h-5 w-5" /> Analizza la mia pelle ora
          </button>
        </Reveal>
      </section>

      {/* ═══════════ ANTEPRIMA REPORT ═══════════ */}
      <section className="border-y border-[#E8E6E1] bg-white">
        <div className="mx-auto max-w-7xl px-6 py-16 sm:px-10 lg:px-14">
          <Reveal className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C17F59]">Ecco cosa ricevi</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl leading-tight">
              Un vero <span className="text-[#C17F59]">documento di consulenza</span>, non un punteggio a caso.
            </h2>
            <p className="mt-4 text-[#5f5a55] leading-relaxed">
              Glow Score, profilo cutaneo, indicatori clinici, analisi aging zona per zona e il tuo obiettivo
              principale con il piano per raggiungerlo. Questo è un esempio reale di report — i tuoi risultati saranno
              personalizzati sulla tua pelle.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="mt-12">
            <ReportPreview />
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <button data-testid="preview-cta-scroll" onClick={scrollToForm}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C17F59] px-8 py-4 text-white font-medium
                transition-all hover:bg-[#a96a47] active:scale-95">
              <Sparkles className="h-5 w-5" /> Voglio il mio report gratuito
            </button>
          </Reveal>
        </div>
      </section>

      {/* ═══════════ PRODOTTI REALI + PROVA SOCIALE ═══════════ */}
      <section className="relative overflow-hidden border-y border-[#E8E6E1] bg-[#2D2825] text-white">
        <img src={HERO} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2D2825] via-[#2D2825]/85 to-[#2D2825]/40" />
        <div className="relative mx-auto max-w-7xl px-6 py-16 sm:px-10 lg:px-14">
          <Reveal className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C17F59]">La routine che ti consigliamo</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl leading-tight">
              Gli stessi cosmetici professionali usati nei centri estetici.
            </h2>
            <p className="mt-4 text-white/75 leading-relaxed">
              Formule professionali, texture piacevoli, risultati incredibili. Il tuo report ti suggerisce solo
              i prodotti giusti per la tua pelle — niente scaffali infiniti, solo ciò che serve davvero a te.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══════════ TESTIMONIANZE ═══════════ */}
      <section className="mx-auto max-w-7xl px-6 py-16 sm:px-10 lg:px-14">
        <Reveal>
          <div className="flex items-center gap-2 text-[#D4AF37]">
            {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-5 w-5 fill-current" />)}
          </div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl leading-tight">Chi ha già scoperto il suo Glow Score</h2>
        </Reveal>
        <Reveal delay={0.1} className="mt-8">
          <Testimonials count={2} />
        </Reveal>
      </section>

      {/* ═══════════ FAQ ═══════════ */}
      <section className="border-t border-[#E8E6E1] bg-white">
        <div className="mx-auto max-w-3xl px-6 py-16 sm:px-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C17F59]">Domande frequenti</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl leading-tight">Tutto chiaro, prima di iniziare</h2>
          </Reveal>
          <div className="mt-8 space-y-3">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.05}>
                <div className="overflow-hidden rounded-2xl border border-[#E8E6E1]">
                  <button data-testid={`faq-toggle-${i}`} onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="flex w-full items-center justify-between gap-4 bg-[#FAF9F6] px-5 py-4 text-left transition-colors hover:bg-[#F3F1EC]">
                    <span className="font-medium text-[#2D2825]">{f.q}</span>
                    <ChevronDown className={`h-5 w-5 shrink-0 text-[#C17F59] transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {openFaq === i && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }} className="overflow-hidden bg-white">
                        <p className="px-5 py-4 text-sm leading-relaxed text-[#5f5a55]">{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ CTA FINALE ═══════════ */}
      <section className="mx-auto max-w-5xl px-6 pb-40 pt-16 sm:px-10 lg:pb-24">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] border border-[#E8E6E1] bg-gradient-to-br from-[#C17F59] to-[#a96a47] px-8 py-14 text-center text-white sm:px-14"
            style={{ boxShadow: "0 24px 60px rgba(193,127,89,0.30)" }}>
            <Sparkles className="mx-auto h-8 w-8" />
            <h2 className="mt-4 font-display text-3xl sm:text-4xl leading-tight">Pronta a scoprire il tuo Glow Score?</h2>
            <p className="mx-auto mt-3 max-w-xl text-white/85">
              Due selfie, qualche minuto e una consulenza professionale gratuita sulla tua pelle. Senza impegno.
            </p>
            <button data-testid="final-cta-scroll" onClick={scrollToForm}
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 font-semibold text-[#2D2825]
                transition-all hover:bg-[#FAF9F6] active:scale-95">
              <Camera className="h-5 w-5" /> Ottieni la mia analisi gratuita
            </button>
            <p className="mt-5 inline-flex items-center gap-1.5 text-xs text-white/75">
              <Lock className="h-3.5 w-3.5" /> Dati protetti · conservati al massimo 30 giorni
            </p>
          </div>
        </Reveal>
      </section>

      {/* ═══════════ LOADING OVERLAY ═══════════ */}
      <AnimatePresence>
        {loading && (
          <motion.div data-testid={GLOW.loadingState} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#FAF9F6]/90 backdrop-blur-md">
            <motion.div animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 1.6, repeat: Infinity }}>
              <Sparkles className="h-10 w-10 text-[#C17F59]" />
            </motion.div>
            <p className="mt-6 font-display text-2xl text-[#2D2825]">Invio in corso…</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
